import heic2any from 'heic2any';
import { RcFile } from 'antd/es/upload';
// @ts-ignore
import { Convertor } from 'encryption-utils';

export const convertHeicToJpeg = async (file: File): Promise<File | undefined> => {
    const buffer = await file.arrayBuffer();
    const blob = new Blob([buffer], { type: 'image/heic' });

    try {
        const jpgBlob = (await heic2any({ blob, toType: 'image/jpeg' })) as Blob; // Convert HEIC to JPEG
        return new File([jpgBlob], file.name.replace(/\.HEIC$/i, '.jpeg'), { type: 'image/jpeg' });
    } catch (error: any) {
        if (error.code === 1 && error.message.includes('image/jpeg')) {
            return new File([blob], file.name.replace(/\.HEIC$/i, '.jpeg'), { type: 'image/jpeg' });
        }
        return undefined;
    }
};

const getExtension = (fileName: string) => {
    const re = /(?:\.([^.]+))?$/;
    return re.exec(fileName)![1];
};

export const getFileExtension = (fileName: string) => {
    const ext = getExtension(fileName);
    return ext === undefined ? 'NA' : ext.toUpperCase().trim();
};

export const getFileName = (fileName: string) => {
    if (!fileName) return fileName;
    const idx = fileName.lastIndexOf('.');
    return idx >= 0 ? fileName.substr(0, fileName.lastIndexOf('.')) : fileName;
};

// suspicious if file has empty name or no extension
export const hasSuspiciousFileName = (fileName: string) => {
    const nameWithoutExtension = getFileName(fileName);
    return nameWithoutExtension === '' || !fileName.includes('.');
};

// note that some extensions do not have mime types like scn and tpl
export const FILE_MIME_TYPE: { [key: string]: string | string[] } = {
    // images
    JPG: 'image/jpeg',
    JPEG: 'image/jpeg',
    BMP: 'image/bmp',
    GIF: 'image/gif',
    PNG: 'image/png',
    HEIC: ['image/heic', ''], // windows mime type is empty for heic files

    // audio
    MP3: 'audio/mpeg',
    WAV: 'audio/wav',
    M4A: 'audio/m4a',
    AAC: 'audio/aac',

    // video
    WMV: 'video/x-ms-wmv',
    MOV: 'video/quicktime',
    AVI: 'video/x-msvideo',
    FLV: 'video/x-flv',
    MP4: 'video/mp4',

    // word
    DOC: 'application/msword',
    DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',

    // excel
    XLS: 'application/vnd.ms-excel',
    XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',

    // ppt
    PPT: 'application/vnd.ms-powerpoint',
    PPTX: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',

    // model
    OBJ: 'model/obj',
    ZIP: ['application/zip', 'application/x-zip-compressed', 'application/octet-stream'],

    // some ifc sample files have empty file types
    // IFC: 'application/x-step',

    // scan
    GLB: ['model/gltf-binary', ''], // glb files exported from app has empty type

    // others
    CSV: 'text/csv',
    PDF: 'application/pdf',
    TXT: 'text/plain',
};

// not all files have mime types. if mime type is available, check that extension matches mime type
export const hasSuspiciousMimeType = (file: RcFile) => {
    const fileExt = getFileExtension(file.name);
    if (fileExt && FILE_MIME_TYPE[fileExt]) {
        if (Array.isArray(FILE_MIME_TYPE[fileExt])) {
            return !FILE_MIME_TYPE[fileExt].includes(file.type);
        }
        return file.type !== FILE_MIME_TYPE[fileExt];
    }
    return false;
};

export const isMaliciousFile = (file: RcFile) => hasSuspiciousFileName(file.name) || hasSuspiciousMimeType(file);

// Chunking Utils
const DEFAULT_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB

export interface GetChunkAtIndexParams {
    /** The file to get the chunk from */
    file: Blob;
    /** The index of the chunk to get, starts at 0 for the first chunk. */
    index: number;
    /** The offset of the chunk. The first chunk will begin at byte offset. Default is 0. */
    offset?: number;
    /** The size of the chunk. Default is 5MB. */
    chunkSize?: number;
}

/**
 * Gets the chunk at specified index.
 * @see GetChunkAtIndexParams for parameters
 *
 * @returns chunk A Blob representing the chunk of the file.
 * @returns null If the index is out of bounds.
 */
export const getChunkAtIndex = (params: GetChunkAtIndexParams) => {
    const { file, index, offset = 0, chunkSize = DEFAULT_CHUNK_SIZE } = params;
    if (offset >= file.size) {
        return null;
    }
    const numberOfChunks = Math.ceil((file.size - offset) / chunkSize);
    if (index >= numberOfChunks) {
        return null;
    }
    const startByte = offset + index * chunkSize;
    const endByte = Math.min(startByte + chunkSize, file.size);
    return file.slice(startByte, endByte);
};

// Hashing Utils
/**
 * Calculates the SHA256 hash of a file.
 * @param file File to calculate the hash of.
 * @param chunkSize The size of the chunk. Default is 5MB.
 * @returns Base-64 encoded string of the SHA256 hash.
 */
export const getSha256Hash = async (
    file: Blob,
    chunkSize = DEFAULT_CHUNK_SIZE,
    onChunkHash?: (i: number, numberOfChunks: number) => void
): Promise<string> => {
    const { process, finish } = Convertor.sha256File();
    const numberOfChunks = Math.ceil(file.size / chunkSize);

    for (let i = 0; i < numberOfChunks; i++) {
        // Guaranteed to be non-null
        const params: GetChunkAtIndexParams = {
            file,
            index: i,
            offset: 0,
            chunkSize,
        };
        const chunk = getChunkAtIndex(params) as Blob;
        // Order of processing chunks is important
        // eslint-disable-next-line no-await-in-loop
        const buffer = (await chunk.arrayBuffer()) as ArrayBuffer;
        process(buffer);
        onChunkHash?.(i, numberOfChunks);
    }
    return Convertor.bytesToBase64(finish());
};

// File Upload Utils
/**
 * Parses offset from the response headers.
 *
 * @param responseHeaders - The response header, which may contain a 'Range' header.
 * @returns The offset, which is the next byte to be uploaded
 */
export const getOffset = (responseHeaders: { range?: string }): number => {
    if (!responseHeaders.range) {
        return 0;
    }
    const lastUploadedByte = parseInt(responseHeaders.range.split('-')[1], 10);
    if (lastUploadedByte === 0) {
        return 0;
    }
    return lastUploadedByte + 1;
};
